<script setup lang="ts">
import type {LegalDocItem} from '@/ts/types/dto/tos-dto';
import SignUpAgreementCheck from '@/vue/organisms/sign-up/sign-up-agreement-check.vue';
import HtmlFormatted from '@/vue/molecules/html-formatted.vue';
import BalmDialog from '@/vue/atoms/balm-dialog.vue';
import {inject, nextTick, ref} from 'vue';
import {consentSelectionKey} from '@/ts/composables/pure/use-consent-selection';
import {findScrollContainer} from '@/ts/composables/pure/use-scroll-to';

defineProps<{
  terms: LegalDocItem;
  privacy: LegalDocItem;
}>();

// Provided by public-sign-up-page so the checkboxes and the stepper footer
// share a single selection state. This form is only ever rendered inside that
// page, so the provider is guaranteed — hence the non-null assertion.
const consent = inject(consentSelectionKey)!;
const {
  termsAccepted,
  appUseAccepted,
  productImprovement,
  analyticsAnonymized,
  research,
  optionalExpanded,
  toggleOptional,
} = consent;

const privacyDialogOpened = ref<boolean>(false);

// Bottom anchor used to reveal the optional consents after the footer
// "Accept All" CTA selects them, so the user can see the result of their choice.
const optionalEndAnchor = ref<HTMLElement>();
// Set when the CTA expands the optional group from collapsed: the actual scroll
// is deferred to the slide-transition's `@show` so it lands once the group has
// finished animating open (scrolling mid-animation would undershoot the final
// scrollHeight).
let scrollWhenShown = false;

// A gentle, deliberately slow reveal reads better here than the browser's
// native (fast, non-configurable) smooth scroll.
const SCROLL_DURATION_MS = 1400;
// Stop short of the very bottom so the reveal doesn't slam against the end of
// the content — leaves the last stretch of the page unscrolled.
const SCROLL_BOTTOM_OFFSET_PX = 160;

// Ease-out: steady pace then decelerates to a gentle stop at the end.
function easeOutCubic(t: number): number {
  return 1 - (1 - t) ** 3;
}

function animateScrollTop(container: HTMLElement, to: number, startTime: number): void {
  const from = container.scrollTop;
  const distance = to - from;
  if (distance === 0) {
    return;
  }
  const step = (now: number): void => {
    const progress = Math.min((now - startTime) / SCROLL_DURATION_MS, 1);
    container.scrollTop = from + distance * easeOutCubic(progress);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };
  requestAnimationFrame(step);
}

function scrollToOptionalEnd(): void {
  const anchor = optionalEndAnchor.value;
  if (!anchor) {
    return;
  }
  // Scroll the card to its very bottom. The footer is sticky but in-flow at the
  // end of the scroll container, so at max scroll the optional consents sit
  // above it (block:'end' alone would land them behind the sticky footer).
  const scrollContainer = findScrollContainer(anchor);
  if (!scrollContainer) {
    return;
  }
  // Max scroll position is scrollHeight - clientHeight; stop a bit above it.
  const maxScrollTop = scrollContainer.scrollHeight - scrollContainer.clientHeight;
  const target = Math.max(maxScrollTop - SCROLL_BOTTOM_OFFSET_PX, 0);
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    scrollContainer.scrollTop = target;
    return;
  }
  animateScrollTop(scrollContainer, target, performance.now());
}

function onOptionalShown(): void {
  if (!scrollWhenShown) {
    return;
  }
  scrollWhenShown = false;
  scrollToOptionalEnd();
}

/**
 * Called by the page when a footer CTA sets the optional consents. If the group
 * was already open we can scroll right away; otherwise we wait for the expand
 * animation to finish (`onOptionalShown`).
 */
function revealOptionalResult(wasExpanded: boolean): void {
  if (wasExpanded) {
    void nextTick(scrollToOptionalEnd);
  } else {
    scrollWhenShown = true;
  }
}

defineExpose({revealOptionalResult});
</script>
<template>
  <div class="sign-up-consent-form">
    <h2 class="h1">
      {{ $t('consent') }}
    </h2>
    <p class="consent-p">
      {{ $t('consent_body') }}
    </p>
    <!--
      The two consents below are required, but pass :optional="true" so
      input-required-checkbox skips its requiredTrue field rule. Gating is
      handled by the footer "Accept and Continue" CTA (disabled until both are
      ticked), not by form validation — there is no q-form wrapper here. Do not
      remove :optional or a dead, never-shown "Required" rule is reintroduced.
    -->
    <sign-up-agreement-check
      v-model="termsAccepted"
      :optional="true"
      :modal-html="terms.legalCopyHTML"
      :header="$t('terms_of_use_star')"
      :text="$t('i_accept_the')"
      :further-info-key="$t('terms_of_use') + '.*'"
    />
    <sign-up-agreement-check
      v-model="appUseAccepted"
      class="required-consent"
      :optional="true"
      :modal-html="privacy.legalCopyHTML"
      :header="$t('health_data')"
      :text="$t('i_agree_to_health_data')"
      :further-info-key="$t('privacy_policy') + '.*'"
    />

    <hr class="consent-divider"/>

    <section class="optional-preferences">
      <h3 class="optional-title">
        {{ $t('optional_preferences') }}
      </h3>
      <p class="optional-intro">
        {{ $t('optional_preferences_intro') }}
      </p>
      <q-slide-transition @show="onOptionalShown">
        <div v-show="optionalExpanded" class="optional-list">
          <sign-up-agreement-check
            v-model="productImprovement"
            class="optional-consent"
            :modal-html="privacy.legalCopyHTML"
            :header="$t('product_improvement')"
            :text="$t('i_agree_to_product_improvement')"
            :optional="true"
          />
          <sign-up-agreement-check
            v-model="analyticsAnonymized"
            class="optional-consent"
            :modal-html="privacy.legalCopyHTML"
            :header="$t('analytics_anonymized')"
            :text="$t('i_agree_to_analytics_anonymized')"
            :optional="true"
          />
          <sign-up-agreement-check
            v-model="research"
            class="optional-consent"
            :modal-html="privacy.legalCopyHTML"
            :header="$t('anonymization')"
            :text="$t('i_agree_to_anonymization')"
            :optional="true"
          />
        </div>
      </q-slide-transition>
      <span
        role="button"
        tabindex="0"
        class="further-info optional-toggle"
        @click="toggleOptional"
        @keydown.enter="toggleOptional"
        @keydown.space.prevent="toggleOptional"
      >
        {{ optionalExpanded ? $t('hide_optional_preferences') : $t('manage_optional_preferences') }}
      </span>
    </section>

    <p ref="optionalEndAnchor" class="consent-p">
      {{ $t('consent_affirmation') }}
      <span
        role="button"
        tabindex="0"
        class="further-info"
        @click.stop="privacyDialogOpened = true"
        @keydown.enter="privacyDialogOpened = true"
        @keydown.space.prevent="privacyDialogOpened = true"
      >
        {{ $t('privacy_policy') }}
      </span>{{ '.' }} <!-- eslint-disable-line @intlify/vue-i18n/no-raw-text -->
      <balm-dialog v-model="privacyDialogOpened" theme="neutral-0">
        <html-formatted :html="privacy.legalCopyHTML" background="neutral-0"/>
      </balm-dialog>
    </p>
  </div>
</template>
<style lang="scss" scoped>
.h1 {
  @include font-display-4;
  margin-bottom: var(--Spacing-spacing-6);
}

.required-consent {
  margin-top: var(--Spacing-spacing-4);
}

.consent-divider {
  border: none;
  border-top: 1px solid var(--Neutral-UI-neutral-ui-30);
  margin: var(--Spacing-spacing-6) 0;
}

.optional-title {
  @include font-body-bold;
  margin-bottom: var(--Spacing-spacing-2);
}

.optional-intro {
  margin: 0;
}

.optional-consent {
  margin-top: var(--Spacing-spacing-4);
}

.optional-toggle {
  display: inline-block;
  margin-top: var(--Spacing-spacing-3);
}

.further-info {
  @include focus-visible;
  text-decoration: underline;
  cursor: pointer;
}

.consent-p {
  margin-top: var(--Spacing-spacing-4);
}
</style>
